import { useState, useRef, useEffect, useCallback } from 'react';
import ChatButton from './ChatButton';
import ChatWindow from './ChatWindow';
import './ChatWidget.css';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatLoaded, setIsChatLoaded] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(localStorage.getItem('login')));

  const iframeRef = useRef(null);
  const chatWindowRef = useRef(null);
  const chatButtonRef = useRef(null);
  const credentialsRef = useRef(null); // cache so we don't refetch on every open
  const currentUserIdRef = useRef(null);

  // ---- credential fetching -------------------------------------------
  const loadChatCredentials = useCallback(async (userId) => {
    const params = new URLSearchParams({ siteID: 'bellagio' });
    if (userId) params.set('userID', userId);

    const response = await fetch(`api/tencent/chatwidget/login?${params}`);
    const creds = await response.json();
    if (creds && creds.success === true) {
      creds.type = 'INIT_CHAT';
    }
    return creds;
  }, []);

  const pushCredentialsToIframe = useCallback(() => {
    if (credentialsRef.current && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(credentialsRef.current, 'https://chat.itone.biz');
    }
  }, []);

  // Loads (or reloads) the iframe with fresh credentials for the given user.
  // Reassigning src forces a full reload, which is what we want when the
  // identity changes (login/logout) so guest and user sessions never mix.
  const initChat = useCallback(async (userId) => {
    try {
      credentialsRef.current = await loadChatCredentials(userId);
      currentUserIdRef.current = userId || null;
    } catch (error) {
      console.error('Chat Cred Error:', error);
      return;
    }

    if (iframeRef.current) {
      iframeRef.current.onload = pushCredentialsToIframe;
      // eslint-disable-next-line no-self-assign
      iframeRef.current.src = 'https://chat.itone.biz';
      setIsChatLoaded(true);
    }
  }, [loadChatCredentials, pushCredentialsToIframe]);

  // ---- open / close -----------------------------------------------------
  const openChat = useCallback(() => {
    setIsOpen(true);
    if (!isChatLoaded) {
      const userId = localStorage.getItem('login');
      initChat(userId); // first-ever open — full INIT_CHAT, credential fetch handles read state itself
    } else {
      iframeRef.current?.contentWindow?.postMessage({ type: 'CHAT_PANEL_REOPENED' }, 'https://chat.itone.biz');
      iframeRef.current?.contentWindow?.postMessage({ type: 'CLEAR_UNREAD' }, 'https://chat.itone.biz');
    }
  }, [isChatLoaded, initChat]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    if (isChatLoaded) {
      iframeRef.current?.contentWindow?.postMessage({ type: 'CHAT_PANEL_CLOSED' }, 'https://chat.itone.biz');
    }
  }, [isChatLoaded]);
  const toggleChat = useCallback(() => (isOpen ? closeChat() : openChat()), [isOpen, openChat, closeChat]);

  // ---- login / logout bridge --------------------------------------------
  // Login flows dispatch site:login/site:logout. Keep itone:* listeners too
  // for backward compatibility with any older emitters.
  useEffect(() => {
    const onLogin = (e) => {
      const userId = e.detail?.userId;
      setIsLoggedIn(Boolean(userId || localStorage.getItem('login')));
      if (userId && userId !== currentUserIdRef.current) {
        initChat(userId);
      }
    };

    const onLogout = () => {
      setIsLoggedIn(false);
      setIsOpen(false);
      currentUserIdRef.current = null;
      credentialsRef.current = null;
      setIsChatLoaded(false); // forces a fresh guest session next time chat opens
    };

    window.addEventListener('site:login', onLogin);
    window.addEventListener('site:logout', onLogout);
    window.addEventListener('itone:login', onLogin);
    window.addEventListener('itone:logout', onLogout);
    return () => {
      window.removeEventListener('site:login', onLogin);
      window.removeEventListener('site:logout', onLogout);
      window.removeEventListener('itone:login', onLogin);
      window.removeEventListener('itone:logout', onLogout);
    };
  }, [initChat]);

  // ---- outside click / escape to close -----------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        chatWindowRef.current &&
        !chatWindowRef.current.contains(event.target) &&
        chatButtonRef.current &&
        !chatButtonRef.current.contains(event.target)
      ) {
        closeChat();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeChat();
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeChat]);

  return (
    <>
      {isLoggedIn && (
        <div ref={chatButtonRef}>
          <ChatButton onClick={toggleChat} />
        </div>
      )}

      <ChatWindow ref={chatWindowRef} isOpen={isOpen} onClose={closeChat} iframeRef={iframeRef} />
    </>
  );
}